import { useMemo } from 'react';

import { Box } from '@mui/material';
import { useSearchParams } from 'react-router';

import { FACTSHEET_TAB_PARAM_NAME, TAB_NAMES, TAB_POSITIONS } from '@/features/Factsheet/constants';
import type { FactsheetDetails, FactsheetTab } from '@/features/Factsheet/types';
import { hasConfigField, hasVersionHistoryField } from '@/features/Factsheet/utils/versionHistory';
import { useStorageValue } from '@/services/storage/react';
import Tabs from '@/shared/ui/primitives/Tabs';
import TabPanel from '@/shared/ui/primitives/Tabs/TabPanel';

import ApproachTab from './Tabs/ApproachTab';
import ChartTab from './Tabs/ChartTab';
import CriteriaTab from './Tabs/CriteriaTab';
import DetailsTab from './Tabs/DetailsTab';
import FlowchartTab from './Tabs/FlowchartTab';
import ItemsTab from './Tabs/ItemsTab';
import QualitySpecTab from './Tabs/QualitySpecTab';
import RelatedCodesTab from './Tabs/RelatedCodesTab';
import UnregisteredBanner from './UnregisteredBanner';

interface FactsheetBodyProps {
  factsheet: FactsheetDetails;
  openNewTab: (code: string) => void;
}

/**
 * Builds the array of tabs based on factsheet content and user authentication.
 */
function buildContentTabs(
  factsheet: FactsheetDetails,
  isAuthenticated: boolean,
  openNewTab: (code: string) => void
): FactsheetTab[] {
  const tabs: FactsheetTab[] = [
    {
      position: TAB_POSITIONS.DETAILS,
      name: TAB_NAMES.DETAILS,
      component: (
        <DetailsTab
          factsheet={factsheet}
          isAuthenticated={isAuthenticated}
        />
      )
    },
    {
      position: TAB_POSITIONS.APPROACH,
      name: TAB_NAMES.APPROACH,
      component: <ApproachTab factsheet={factsheet} />
    },
    {
      position: TAB_POSITIONS.FLOWCHART,
      name: TAB_NAMES.FLOWCHART,
      component: (
        <FlowchartTab
          factsheet={factsheet}
          openNewTab={openNewTab}
        />
      )
    },
    {
      position: TAB_POSITIONS.RELATED_CODES,
      name: TAB_NAMES.RELATED_CODES,
      component: (
        <RelatedCodesTab
          factsheet={factsheet}
          openNewTab={openNewTab}
        />
      )
    }
  ];

  // Conditionally add chart tab
  if (factsheet.spotSymbols.length > 0) {
    tabs.push({
      position: TAB_POSITIONS.CHART,
      name: TAB_NAMES.CHART,
      component: <ChartTab factsheet={factsheet} />
    });
  }

  // Conditionally add quality spec tab
  const shouldShowConfigTab = (
    tabName: 'qualitySpecification' | 'criteriaForInclusion' | 'calculationApproach'
  ) => {
    return hasConfigField(factsheet, tabName) || hasVersionHistoryField(factsheet, tabName);
  };
  if (shouldShowConfigTab('qualitySpecification')) {
    tabs.push({
      position: TAB_POSITIONS.QUALITY_SPEC,
      name: TAB_NAMES.QUALITY_SPEC,
      component: (
        <QualitySpecTab
          factsheet={factsheet}
          isAuthenticated={isAuthenticated}
        />
      )
    });
  }

  // Conditionally add criteria tab
  if (shouldShowConfigTab('criteriaForInclusion')) {
    tabs.push({
      position: TAB_POSITIONS.CRITERIA,
      name: TAB_NAMES.CRITERIA,
      component: <CriteriaTab factsheet={factsheet} />
    });
  }

  // Conditionally add items tab
  if (factsheet.symbols.length > 0) {
    tabs.push({
      position: TAB_POSITIONS.AVAILABLE_ITEMS,
      name: TAB_NAMES.AVAILABLE_ITEMS,
      component: <ItemsTab factsheet={factsheet} />
    });
  }

  return tabs.toSorted((a, b) => a.position - b.position);
}

const FactsheetBody = ({ factsheet, openNewTab }: FactsheetBodyProps) => {
  const gxToken = useStorageValue('auth.gxToken');
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();

  const isAuthenticated = Boolean(gxToken);

  const contentTabs = useMemo(
    () => buildContentTabs(factsheet, isAuthenticated, openNewTab),
    [factsheet, isAuthenticated, openNewTab]
  );

  const updateActiveTabParam = (tabIndex: number): void => {
    const tabName = contentTabs[tabIndex]?.name ?? '';
    const newSearchParams = new URLSearchParams(urlSearchParams);
    newSearchParams.set(FACTSHEET_TAB_PARAM_NAME, tabName);
    setUrlSearchParams(newSearchParams);
  };

  const activeTab = useMemo(() => {
    const activeTabParam = decodeURIComponent(urlSearchParams.get(FACTSHEET_TAB_PARAM_NAME) ?? '');
    if (activeTabParam.length === 0) return 0;
    const index = contentTabs.findIndex(tab => tab.name === activeTabParam);
    return Math.max(index, 0);
  }, [contentTabs, urlSearchParams]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        flexDirection: 'column',
        flex: 1,
        px: 3,
        flexGrow: 1,
        overflow: 'hidden',
        width: '100%'
      }}
    >
      <Box
        sx={{
          mt: 3,
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%'
        }}
      >
        <Tabs
          activeTab={activeTab}
          scrollButtons='auto'
          tabs={contentTabs.map(({ name }) => ({ name }))}
          onChange={updateActiveTabParam}
        />
      </Box>
      {contentTabs.map((tab, index) => (
        <TabPanel
          key={`${factsheet.code}-${tab.name}`}
          containerSX={{
            flex: 1,
            flexGrow: 1,
            width: '100%',
            overflow: 'hidden',
            ...(index === activeTab ? { display: 'flex' } : {})
          }}
          index={index}
          sx={{
            flex: 1,
            flexGrow: 1,
            width: '100%',
            overflow: 'auto'
          }}
          value={activeTab}
        >
          {isAuthenticated || tab.position === TAB_POSITIONS.DETAILS ? (
            tab.component
          ) : (
            <UnregisteredBanner />
          )}
        </TabPanel>
      ))}
    </Box>
  );
};

export default FactsheetBody;
